import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import set from "../../assests/settings.png";
import spoon from "../../assests/spoons.svg";
import box from "../../assests/box.svg";
import back from "../../assests/back-w.svg";
import curbside from "../../assests/curbside.svg";
import counter from "../../assests/counter.png"
import call from "../../assests/phone-call.png";
import api, { ApiResponse } from "../../services/api";

interface OrderItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  discountAmount: number;
  customOptions?: { label: string; price: number; qty: number }[];
}

interface Order {
  _id: string;
  orderNumber: string;
  items: OrderItem[];
  choice?: string;
  tableToken?: number;
  createdAt: string;
  orderStatus: string;
}

const statuses = ["all", "received", "preparing", "ready"];
const getOrderNumberLabel = (order: { orderNumber?: string; _id: string }) =>
  order.orderNumber != null
    ? `#${order.orderNumber}`
    : `#${order._id.slice(-6).toUpperCase()}`;

const getStatusColor = (status: string): string => {
  switch (status) {
    case "received":
      return "#7CBF80"; // 🟢 green
    case "preparing":
      return "#F2B55D"; // 🟡 yellow
    default:
      return "#E5E7EB"; // ⚪ fallback gray
  }
};

// Ready Order Card (Kitchen-style)
const ReadyCard: React.FC<{ order: Order }> = ({ order }) => {
  return (
    <div
      key={order._id}
      className="w-80 mt-3 rounded-xl overflow-hidden font-sans shadow-sm bg-[#0f4258] text-white p-3"
    >
      <div className="flex justify-between items-start border-b border-white">
        <div>
          <div className="text-sm font-outfit font-semibold">
            Table {order.tableToken ?? "-"}
          </div>
          <div className="text-xs font-semibold font-outfit text-white/70">
            {new Date(order.createdAt).toLocaleTimeString()}
          </div>
        </div>

        <div className="text-right">
          <div className="font-outfit font-semibold">
            {getOrderNumberLabel(order)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <div className="rounded-full bg-[#6CB670] p-2">
          {order.choice?.toLowerCase() === "dining in" ? (
            <img src={spoon} alt="dine-in" className="w-5 h-5" />
          ) : order.choice?.toLowerCase() === "takeaway" ? (
            <img src={box} alt="takeaway" className="w-5 h-5" />
          ) : (
            <img src={counter} alt="counter" className="w-5 h-5" />
          )}
        </div>
        <div className="text-sm font-outfit">
          {order.choice ?? "CounterSide"}
        </div>

        <div className="bg-[#c7d8e069] text-white px-2 py-1 rounded-full text-xs ml-auto flex gap-1 cursor-pointer">
          <img
            src={call}
            alt="Logo"
            className="w-[15px] h-[15px] bg-white rounded-full cursor-pointer"
          />
          Buzzer
        </div>
      </div>

      <div className="mt-4 text-white/90 max-h-52 overflow-y-auto no-scrollbar">
        {order.items.map((item, idx) => (
          <div key={`${order._id}-${item.itemId}-${idx}`} className="mb-2">
            <div className="text-lg font-semibold">
              {item.quantity} x {item.name}
            </div>
            {item.customOptions?.length ? (
              <div className="text-xs text-white/70 mt-1">
                {item.customOptions.map((opt, j) => (
                  <div key={j}>
                    {opt.label} (x{opt.qty})
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const Notification: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [activeStatus, setActiveStatus] = useState<string>("all");
  const navigate = useNavigate();

  // Fetch orders from API
 useEffect(() => {
  const fetchOrders = async () => {
    try {
      const response = await api.get<ApiResponse<Order[]>>("/orders");

      if (response.data.success) {
        const today = new Date();
        const startOfDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          0, 0, 0, 0
        );
        const endOfDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23, 59, 59, 999
        );

        const filtered = response.data.data.filter((order) => {
          const createdAt = new Date(order.createdAt);
          return (
            order.orderStatus !== "cancelled" &&
            createdAt >= startOfDay &&
            createdAt <= endOfDay
          );
        });

        setOrders(filtered);
      }
    } catch (err) {
      console.error("Failed to fetch orders", err);
    }
  };

  fetchOrders();
}, []);


  // Filter orders based on selected status
  const filteredOrders =
    activeStatus === "all"
      ? orders
      : orders.filter((order) => order.orderStatus === activeStatus);

  // Count "received" orders for badge
  const receivedOrderCount = orders.filter(
    (order) => order.orderStatus === "received"
  ).length;

  return (
    <>
      <div className="bg-[#3E3E3E] h-[90px] w-full flex items-center justify-between px-3 ">
        <div className=" flex gap-2 text-white font-outfit">
          <div onClick={() => navigate("/kitchen")}>
            <img src={back} alt="back" className="h-9 w-8" />
          </div>
          <h3>Notification</h3>
        </div>

        <div className="flex items-center gap-4">
          <div className="h-10 text-white flex items-center gap-3 justify-center">
            <img
              src={set}
              alt="Settings"
              className="w-[30px] h-[30px] bg-[#636363] rounded-full"
              onClick={() => navigate("/kitchen/setting")}
            />
          </div>
        </div>
      </div>

      <div className="h-[70px] w-11/12 bg-[#ECF0F2] mx-auto mt-4 flex flex-row gap-x-2.5 p-2">
        {statuses.map((status) => {
          const isAll = status === "all";
          const label = isAll
            ? "All Orders"
            : status.charAt(0).toUpperCase() + status.slice(1);

          return (
            <div
              key={status}
              onClick={() => setActiveStatus(status)}
              className={`my-auto p-2 px-3 rounded-md cursor-pointer font-outfit relative ${
                activeStatus === status ? "bg-[#C7D8E0]" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                {label}
                {status === "received" && receivedOrderCount > 0 && (
                  <div className="absolute top-[-8px] right-[-8px] bg-red-500 text-white rounded-full text-sm w-5 h-5 font-outfit flex items-center justify-center">
                    {receivedOrderCount}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-4  justify-start px-5  mt-5">
        {filteredOrders.map((order) =>
          order.orderStatus === "ready" ? (
            <ReadyCard key={order._id} order={order} />
          ) : (
            <div
              key={order._id}
              className="w-80 mt-3 border border-gray-200 rounded-xl overflow-hidden font-sans shadow-sm bg-white flex flex-col"
            >
              <div
                className="p-2 text-white font-semibold text-sm"
                style={{
                  backgroundColor:getStatusColor(order.orderStatus),
                }}
              >
                <div className="flex justify-between">
                  <span className="font-outfit">Order</span>
                  <span className="text-right font-outfit">
                    {getOrderNumberLabel(order)}
                  </span>
                </div>
                <div className="flex justify-between">
                  {order.choice && (
                    <div className="flex items-center gap-2">
                      <span className="text-s font-outfit text-white/80">
                        {order.choice.toUpperCase()}
                      </span>
                      {order.choice.toLowerCase() === "dining in" && (
                        <img
                          src={spoon}
                          alt="Dining In"
                          className="w-[20px] h-[20px]"
                        />
                      )}
                      {order.choice.toLowerCase() === "takeaway" && (
                        <img
                          src={box}
                          alt="Takeaway"
                          className="w-[20px] h-[20px]"
                        />
                      )}
                      {order.choice.toLowerCase() === "curbside" && (
                        <img
                          src={curbside}
                          alt="Takeaway"
                          className="w-[20px] h-[20px]"
                        />
                      )}
                    </div>
                  )}
                  <span className="text-right font-outfit">
                    {order.orderStatus}
                  </span>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 max-h-52 no-scrollbar">
                {order.items.map((item, idx) => (
                  <div
                    key={`${order._id}-${item.itemId}-${idx}`}
                    className="p-4 border-b border-gray-200 text-sm flex justify-between items-center"
                  >
                    <span className="font-outfit text-[#113E60]">
                      <b>{item.quantity}x</b> {item.name}
                      {item.customOptions && item.customOptions.length > 0 && (
                        <div className="ml-2 text-xs text-[#113E60]">
                          {item.customOptions.map((opt, j) => (
                            <p className="mb-0 font-outfit" key={j}>
                              {opt.label} (x{opt.qty})
                            </p>
                          ))}
                        </div>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div>
                <div className="px-3 py-2 text-xs text-[#113E60] font-outfit">
                  Ordered at: {new Date(order.createdAt).toLocaleTimeString()}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default Notification;
